import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, X, Loader2, Building2, User, Mail, Phone, Briefcase } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  { id: 'strategy', label: 'Strategia di Marketing Digitale' },
  { id: 'social', label: 'Social Media Management' },
  { id: 'adv', label: 'Campagne ADV (Meta, Google, TikTok)' },
  { id: 'content', label: 'Content & Creative Production' },
  { id: 'branding', label: 'Branding & Visual Identity' },
  { id: 'analytics', label: 'Analytics & Performance Tracking' },
  { id: 'full', label: 'Pacchetto Completo' },
];

const steps = [
  { id: 'company', label: 'Come si chiama la tua azienda?', icon: Building2, placeholder: 'Es. Acme S.r.l.', autoComplete: 'organization' },
  { id: 'name', label: 'Come ti chiami?', icon: User, placeholder: 'Es. Marco Rossi', autoComplete: 'name' },
  { id: 'email', label: 'Qual è la tua email?', icon: Mail, placeholder: 'Es. marco@azienda.it', type: 'email', autoComplete: 'email' },
  { id: 'phone', label: 'A che numero possiamo contattarti?', icon: Phone, placeholder: 'Es. +39 333 1234567', type: 'tel', autoComplete: 'tel' },
  { id: 'service', label: 'Di cosa hai bisogno?', icon: Briefcase, isSelect: true },
];

export const TypeformQuote = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    service: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const validateStep = useCallback(() => {
    const value = formData[currentStepData.id];
    if (!value || value.trim() === '') return false;
    
    if (currentStepData.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    }
    
    return true;
  }, [formData, currentStepData]);

  const handleNext = useCallback(async () => {
    if (!validateStep()) {
      setError('Per favore compila questo campo');
      return;
    }
    
    setError('');
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Submit form
      setIsSubmitting(true);
      try {
        const payload = {
          company: formData.company,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service_type: formData.service,
        };
        
        await axios.post(`${API}/leads`, payload);
        setIsSuccess(true);
      } catch (err) {
        console.error('Error submitting form:', err);
        setError('Si è verificato un errore. Riprova più tardi.');
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [currentStep, formData, validateStep]);

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setError('');
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !currentStepData.isSelect) {
      e.preventDefault();
      handleNext();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  }, [handleNext, onClose, currentStepData]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('form-open');
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('form-open');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const handleClose = () => {
    setCurrentStep(0);
    setFormData({ company: '', name: '', email: '', phone: '', service: '' });
    setIsSuccess(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const IconComponent = currentStepData?.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      data-testid="typeform-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/95 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Content */}
      <div className="relative w-full max-w-3xl mx-4 md:mx-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-12 right-0 p-2 text-slate-400 hover:text-white transition-colors"
          data-testid="close-form-button"
        >
          <X size={24} />
        </button>

        <AnimatePresence mode="wait">
          {isSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-16"
              data-testid="form-success"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-20 h-20 mx-auto mb-8 rounded-full bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center"
              >
                <Check size={40} className="text-white" />
              </motion.div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Grtsk_Tera']">
                Perfetto!
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Ti contatteremo entro 24 ore con una proposta su misura.
              </p>
              <button
                onClick={handleClose}
                className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                data-testid="close-success-button"
              >
                Chiudi
              </button>
            </motion.div>
          ) : (
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="py-8 md:py-16"
            >
              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-8 text-slate-400">
                <span className="text-sm">
                  {currentStep + 1} / {steps.length}
                </span>
                {IconComponent && <IconComponent size={16} />}
              </div>

              {/* Question */}
              <h2 className="text-xl md:text-2xl font-bold text-white mb-12 font-['Grtsk_Tera']" data-testid="form-question">
                {currentStepData.label}
              </h2>

              {/* Input */}
              {currentStepData.isSelect ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="service-options">
                  {services.map((service) => (
                    <button
                      key={service.id}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, service: service.label }));
                        setError('');
                      }}
                      className={`form-option text-left text-lg ${
                        formData.service === service.label ? 'selected' : ''
                      }`}
                      data-testid={`service-option-${service.id}`}
                    >
                      <span className="text-white">{service.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  type={currentStepData.type || 'text'}
                  value={formData[currentStepData.id]}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, [currentStepData.id]: e.target.value }));
                    setError('');
                  }}
                  placeholder={currentStepData.placeholder}
                  className="typeform-input"
                  autoFocus
                  autoComplete={currentStepData.autoComplete || 'off'}
                  name={`creativit-${currentStepData.id}`}
                  id={`creativit-${currentStepData.id}`}
                  data-testid={`input-${currentStepData.id}`}
                />
              )}

              {/* Error */}
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 mt-4"
                  data-testid="form-error"
                >
                  {error}
                </motion.p>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-12">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                    currentStep === 0
                      ? 'text-slate-600 cursor-not-allowed'
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="back-button"
                >
                  <ArrowLeft size={20} />
                  <span>Indietro</span>
                </button>

                <button
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className="cta-button flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold disabled:opacity-50"
                  data-testid="next-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>Invio...</span>
                    </>
                  ) : currentStep === steps.length - 1 ? (
                    <>
                      <span>Invia Richiesta</span>
                      <Check size={20} />
                    </>
                  ) : (
                    <>
                      <span>Continua</span>
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress bar */}
        {!isSuccess && (
          <div className="progress-bar mt-8">
            <motion.div
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              data-testid="progress-bar"
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TypeformQuote;
